import React from 'react'
import { Container, Row, Table } from 'react-bootstrap'

const ExpandableHeader = ({ title, maxLength = 25 }) => {
  const [expanded, setExpanded] = React.useState(false)
  if (!title || title.length <= maxLength) return <>{title}</>
  return (
    <span>
      {expanded ? title : title.slice(0, maxLength) + '...'}{' '}
      <div
        className='text-saffron-700 cursor-pointer'
        onClick={() => setExpanded((prev) => !prev)}>
        {expanded ? 'Read less' : 'Read more'}
      </div>
    </span>
  )
}

const SessionDetailsTable = ({ reportData }) => {
  if (!reportData) {
    return (
      <div className='text-center py-4'>
        <h3 className='font-montserrat'>No Report Data Found.</h3>
      </div>
    )
  }

  // Resolve report body whether passed flat or nested under .data (axios-style).
  // Do NOT treat reportData.data as the body when it is the rows array.
  const actualData = reportData.keyValueDataForColumnName
    ? reportData
    : reportData.data?.keyValueDataForColumnName
    ? reportData.data
    : reportData

  const columnMapping = actualData.keyValueDataForColumnName || {}
  const clientsData = Array.isArray(actualData.data) ? actualData.data : []

  const columnKeys = Object.keys(columnMapping)

  if (columnKeys.length === 0 || clientsData.length === 0) {
    return (
      <div className='text-center py-4'>
        <h3 className='font-montserrat'>No Report Data Found.</h3>
      </div>
    )
  }

  // Helper function to handle client navigation
  const handleClientClick = (clientId) => {
    if (clientId) {
      window.open(`/admin/users/${clientId}`, '_blank')
    }
  }

  const isClientIdentityColumn = (columnKey) =>
    columnKey === 'clientName' ||
    columnKey === 'clientEmail' ||
    columnKey === 'name' ||
    columnKey === 'email'

  // Saffron text for user identity columns and entityName (entityName is not clickable)
  const isSaffronColumn = (columnKey) =>
    isClientIdentityColumn(columnKey) || columnKey === 'entityName'

  // Apply name/email/entity fields; values come from BE as-is (no capitalization).
  const applyClientIdentityFields = (rowData, client, showBasicInfo) => {
    const displayName = client.clientName || client.name || ''
    const displayEmail = client.clientEmail || client.email || ''
    const entityType = client.entityType || ''
    const entityName = client.entityName || ''

    rowData.clientName = showBasicInfo ? displayName : ''
    rowData.name = showBasicInfo ? displayName : ''
    rowData.clientEmail = showBasicInfo ? displayEmail : ''
    rowData.email = showBasicInfo ? displayEmail : ''
    rowData.entityType = showBasicInfo ? entityType : ''
    rowData.entityName = showBasicInfo ? entityName : ''
  }

  // Helper function to strip HTML tags - improved with better error handling
  const stripHtml = (html) => {
    if (!html) return '-'

    try {
      // For React Native/Expo environment, we need a different approach
      if (typeof document === 'undefined') {
        // Simple regex-based HTML stripping for React Native
        return html.replace(/<[^>]*>/g, '').trim() || '-'
      }

      const tmp = document.createElement('div')
      tmp.innerHTML = html
      return tmp.textContent || tmp.innerText || '-'
    } catch (error) {
      // Fallback to simple regex stripping
      return html.replace(/<[^>]*>/g, '').trim() || '-'
    }
  }

  // Helper function to find growth question answer - improved to handle the actual data structure
  const findGrowthAnswer = (reviewCalls, questionText) => {
    if (!reviewCalls || !Array.isArray(reviewCalls) || !questionText) return '-'

    const found = reviewCalls.find((call) => call.question === questionText)
    return found ? stripHtml(found.answer) : '-'
  }

  // Helper to format intentions and insights for a single cell
  const formatIntentionsAndInsights = (intentions) => {
    if (!intentions || intentions.length === 0) return '-'
    return intentions
      .map((intention) => {
        const title = intention.title || '-'
        const insights =
          intention.insights && intention.insights.length > 0
            ? intention.insights
                .map((insight) => `  • ${stripHtml(insight)}`)
                .join('\n')
            : '  • -'
        return `${title}:\n${insights}`
      })
      .join('\n\n')
  }

  // Generate table rows
  const generateRows = () => {
    const allRows = []

    clientsData.forEach((client, clientIndex) => {
      const { sessions = [], intentions = [] } = client
      const clientId = client.clientId || client.userId
      let rowCount = 0
      let sessionNumber = 0 // Track session numbers separately
      const totalGroupSessions = sessions.filter((s) => s.isGroupSession).length
      let groupHeaderRendered = false
      // Process sessions
      sessions.forEach((session, sessionIndex) => {
        if (session.isGrowthSession) {
          // Growth Session
          const rowData = {}

          // Basic client info (show only on first row)
          applyClientIdentityFields(rowData, client, rowCount === 0)

          // Session fields
          rowData.sessionNumber = '-'
          rowData.coachName = session.coach || '-'
          rowData.duration = session.duration || '-'
          rowData.postSessionPractices = stripHtml(session.postSessionPractices)
          rowData.nonConfidentialFeedback = stripHtml(
            session.nonConfidentialFeedback,
          )
          rowData.intentions = '-'
          rowData.insights = '-'

          // Group session fields (empty for growth sessions)
          rowData.groupSessionNumber = '-'
          rowData.groupCoach = '-'
          rowData.groupPostSessionPractices = '-'
          rowData.groupNonConfidentialFeedback = '-'

          // Growth session fields
          rowData.growthNumber = sessionIndex + 1
          rowData.growthCoach = session.growthCoach || '-'
          rowData.growthNotes = stripHtml(session.growthNotes)

          // Growth questions - map each question dynamically
          columnKeys.forEach((key) => {
            if (key.startsWith('growthQuestion')) {
              const questionText = columnMapping[key]
              rowData[key] = findGrowthAnswer(session.reviewCall, questionText)
            }
          })

          allRows.push(
            <tr key={`client-${clientIndex}-growth-${sessionIndex}`}>
              {columnKeys.map((columnKey) => (
                <td
                  key={columnKey}
                  style={{
                    ...(isSaffronColumn(columnKey) ? { color: '#bf9000' } : {}),
                    ...(isClientIdentityColumn(columnKey)
                      ? { cursor: 'pointer' }
                      : {}),
                    ...(columnKey === 'intentions'
                      ? { whiteSpace: 'pre-line', textAlign: 'left' }
                      : columnKey === 'insights'
                      ? { whiteSpace: 'pre-line' }
                      : {}),
                  }}
                  onClick={() => {
                    if (
                      isClientIdentityColumn(columnKey) &&
                      rowData[columnKey] &&
                      rowData[columnKey] !== ''
                    ) {
                      handleClientClick(clientId)
                    }
                  }}>
                  {rowData[columnKey] || '-'}
                </td>
              ))}
            </tr>,
          )
          rowCount++
        } else if (session.isGroupSession) {
          // Group Session
          sessionNumber++ // Increment session number for group sessions
          const rowData = {}

          // Basic client info (show only on first row)
          // For group sessions, show name and email once using rowSpan
          const isFirstGroupRow = !groupHeaderRendered
          applyClientIdentityFields(rowData, client, isFirstGroupRow)

          // Regular session fields (empty for group sessions)
          rowData.sessionNumber = '-'
          rowData.coachName = '-'
          rowData.duration = session.duration || '-'
          rowData.postSessionPractices = '-'
          rowData.nonConfidentialFeedback = '-'
          rowData.intentions = '-'
          rowData.insights = '-'

          // Group session fields
          rowData.groupSessionNumber = sessionNumber
          rowData.groupCoach = session.groupCoach || '-'
          rowData.groupPostSessionPractices = stripHtml(
            session.groupPostSessionPractices,
          )
          rowData.groupNonConfidentialFeedback = stripHtml(
            session.groupNonConfidentialFeedback,
          )

          // Growth session fields (empty for group sessions)
          rowData.growthNumber = '-'
          rowData.growthCoach = '-'
          rowData.growthNotes = '-'

          // Growth questions (empty for group sessions)
          columnKeys.forEach((key) => {
            if (key.startsWith('growthQuestion')) {
              rowData[key] = '-'
            }
          })

          allRows.push(
            <tr key={`client-${clientIndex}-group-${sessionIndex}`}>
              {columnKeys.map((columnKey) => {
                const isRowSpannedColumn =
                  isClientIdentityColumn(columnKey) ||
                  columnKey === 'entityType' ||
                  columnKey === 'entityName'

                if (isRowSpannedColumn && !isFirstGroupRow) {
                  return null
                }
                if (isRowSpannedColumn && isFirstGroupRow) {
                  const isClickable = isClientIdentityColumn(columnKey)
                  return (
                    <td
                      key={columnKey}
                      rowSpan={Math.max(totalGroupSessions, 1)}
                      style={{
                        textAlign: 'center',
                        ...(isSaffronColumn(columnKey)
                          ? { color: '#bf9000' }
                          : {}),
                        ...(isClickable ? { cursor: 'pointer' } : {}),
                      }}
                      onClick={() => {
                        if (isClickable) {
                          handleClientClick(clientId)
                        }
                      }}>
                      {rowData[columnKey] || '-'}
                    </td>
                  )
                }
                return (
                  <td
                    key={columnKey}
                    style={{
                      ...(columnKey === 'intentions'
                        ? { whiteSpace: 'pre-line', textAlign: 'left' }
                        : columnKey === 'insights'
                        ? { whiteSpace: 'pre-line' }
                        : {}),
                    }}>
                    {rowData[columnKey] || '-'}
                  </td>
                )
              })}
            </tr>,
          )
          rowCount++
          if (!groupHeaderRendered) groupHeaderRendered = true
        } else {
          // Regular Session
          sessionNumber++ // Increment session number for regular sessions
          const rowData = {}

          // Basic client info (show only on first row)
          applyClientIdentityFields(rowData, client, rowCount === 0)

          // Regular session fields
          rowData.sessionNumber = sessionNumber
          rowData.coachName = session.coach || '-'
          rowData.duration = session.duration || '-'
          rowData.postSessionPractices = stripHtml(session.postSessionPractices)
          rowData.nonConfidentialFeedback = stripHtml(
            session.nonConfidentialFeedback,
          )
          rowData.intentions = '-'
          rowData.insights = '-'

          // Group session fields (empty for regular sessions)
          rowData.groupSessionNumber = '-'
          rowData.groupCoach = '-'
          rowData.groupPostSessionPractices = '-'
          rowData.groupNonConfidentialFeedback = '-'

          // Growth session fields (empty for regular sessions)
          rowData.growthNumber = '-'
          rowData.growthCoach = '-'
          rowData.growthNotes = '-'

          // Growth questions (empty for regular sessions)
          columnKeys.forEach((key) => {
            if (key.startsWith('growthQuestion')) {
              rowData[key] = '-'
            }
          })

          allRows.push(
            <tr key={`client-${clientIndex}-regular-${sessionIndex}`}>
              {columnKeys.map((columnKey) => (
                <td
                  key={columnKey}
                  style={{
                    ...(isSaffronColumn(columnKey) ? { color: '#bf9000' } : {}),
                    ...(isClientIdentityColumn(columnKey)
                      ? { cursor: 'pointer' }
                      : {}),
                    ...(columnKey === 'intentions'
                      ? { whiteSpace: 'pre-line', textAlign: 'left' }
                      : columnKey === 'insights'
                      ? { whiteSpace: 'pre-line' }
                      : {}),
                  }}
                  onClick={() => {
                    if (
                      isClientIdentityColumn(columnKey) &&
                      rowData[columnKey] &&
                      rowData[columnKey] !== ''
                    ) {
                      handleClientClick(clientId)
                    }
                  }}>
                  {rowData[columnKey] || '-'}
                </td>
              ))}
            </tr>,
          )
          rowCount++
        }
      })

      // Process intentions
      if (sessions.length === 0 && intentions.length > 0) {
        const rowData = {}
        applyClientIdentityFields(rowData, client, true)
        rowData.sessionNumber = '-'
        rowData.coachName = '-'
        rowData.duration = '-'
        rowData.postSessionPractices = '-'
        rowData.nonConfidentialFeedback = '-'
        rowData.intentions = intentions.map((i) => i.title || '-').join('\n')
        rowData.insights = formatIntentionsAndInsights(intentions)

        // Group session fields (empty for intentions)
        rowData.groupSessionNumber = '-'
        rowData.groupCoach = '-'
        rowData.groupPostSessionPractices = '-'
        rowData.groupNonConfidentialFeedback = '-'

        rowData.growthNumber = '-'
        rowData.growthCoach = '-'
        rowData.growthNotes = '-'
        columnKeys.forEach((key) => {
          if (key.startsWith('growthQuestion')) {
            rowData[key] = '-'
          }
        })
        allRows.push(
          <tr key={`client-${clientIndex}-intentions-aggregated`}>
            {columnKeys.map((columnKey) => (
              <td
                key={columnKey}
                style={{
                  ...(isSaffronColumn(columnKey) ? { color: '#bf9000' } : {}),
                  ...(isClientIdentityColumn(columnKey)
                    ? { cursor: 'pointer' }
                    : {}),
                  ...(columnKey === 'intentions'
                    ? { whiteSpace: 'pre-line', textAlign: 'left' }
                    : columnKey === 'insights'
                    ? { whiteSpace: 'pre-line' }
                    : {}),
                }}
                onClick={() => {
                  if (
                    isClientIdentityColumn(columnKey) &&
                    rowData[columnKey] &&
                    rowData[columnKey] !== ''
                  ) {
                    handleClientClick(clientId)
                  }
                }}>
                {rowData[columnKey] || '-'}
              </td>
            ))}
          </tr>,
        )
        rowCount++
      } else {
        // Process intentions (original logic)
        intentions.forEach((intention, intentionIndex) => {
          const insights = intention.insights || []

          if (insights.length === 0) {
            // Intention without insights
            const rowData = {}

            applyClientIdentityFields(rowData, client, rowCount === 0)
            rowData.sessionNumber = '-'
            rowData.coachName = '-'
            rowData.duration = '-'
            rowData.postSessionPractices = '-'
            rowData.nonConfidentialFeedback = '-'
            rowData.intentions = intention.title || '-'
            rowData.insights = '-'

            // Group session fields (empty for intentions)
            rowData.groupSessionNumber = '-'
            rowData.groupCoach = '-'
            rowData.groupPostSessionPractices = '-'
            rowData.groupNonConfidentialFeedback = '-'

            rowData.growthNumber = '-'
            rowData.growthCoach = '-'
            rowData.growthNotes = '-'

            columnKeys.forEach((key) => {
              if (key.startsWith('growthQuestion')) {
                rowData[key] = '-'
              }
            })

            allRows.push(
              <tr key={`client-${clientIndex}-intention-${intentionIndex}`}>
                {columnKeys.map((columnKey) => (
                  <td
                    key={columnKey}
                    style={{
                      ...(isSaffronColumn(columnKey)
                        ? { color: '#bf9000' }
                        : {}),
                      ...(isClientIdentityColumn(columnKey)
                        ? { cursor: 'pointer' }
                        : {}),
                      ...(columnKey === 'intentions'
                        ? { whiteSpace: 'pre-line', textAlign: 'left' }
                        : columnKey === 'insights'
                        ? { whiteSpace: 'pre-line' }
                        : {}),
                    }}
                    onClick={() => {
                      if (
                        isClientIdentityColumn(columnKey) &&
                        rowData[columnKey] &&
                        rowData[columnKey] !== ''
                      ) {
                        handleClientClick(clientId)
                      }
                    }}>
                    {rowData[columnKey] || '-'}
                  </td>
                ))}
              </tr>,
            )
            rowCount++
          } else {
            // Intention with insights
            insights.forEach((insight, insightIndex) => {
              const rowData = {}

              applyClientIdentityFields(rowData, client, rowCount === 0)
              rowData.sessionNumber = '-'
              rowData.coachName = '-'
              rowData.duration = '-'
              rowData.postSessionPractices = '-'
              rowData.nonConfidentialFeedback = '-'
              rowData.intentions =
                insightIndex === 0 ? intention.title || '-' : ''
              rowData.insights = stripHtml(insight)

              // Group session fields (empty for intentions)
              rowData.groupSessionNumber = '-'
              rowData.groupCoach = '-'
              rowData.groupPostSessionPractices = '-'
              rowData.groupNonConfidentialFeedback = '-'

              rowData.growthNumber = '-'
              rowData.growthCoach = '-'
              rowData.growthNotes = '-'

              columnKeys.forEach((key) => {
                if (key.startsWith('growthQuestion')) {
                  rowData[key] = '-'
                }
              })

              allRows.push(
                <tr
                  key={`client-${clientIndex}-intention-${intentionIndex}-insight-${insightIndex}`}>
                  {columnKeys.map((columnKey) => (
                    <td
                      key={columnKey}
                      style={{
                        ...(isSaffronColumn(columnKey)
                          ? { color: '#bf9000' }
                          : {}),
                        ...(isClientIdentityColumn(columnKey)
                          ? { cursor: 'pointer' }
                          : {}),
                        ...(columnKey === 'intentions'
                          ? { whiteSpace: 'pre-line', textAlign: 'left' }
                          : columnKey === 'insights'
                          ? { whiteSpace: 'pre-line' }
                          : {}),
                      }}
                      onClick={() => {
                        if (
                          isClientIdentityColumn(columnKey) &&
                          rowData[columnKey] &&
                          rowData[columnKey] !== ''
                        ) {
                          handleClientClick(clientId)
                        }
                      }}>
                      {rowData[columnKey] || '-'}
                    </td>
                  ))}
                </tr>,
              )
              rowCount++
            })
          }
        })
      }

      // If no sessions or intentions, show empty row
      if (sessions.length === 0 && intentions.length === 0) {
        const rowData = {}
        applyClientIdentityFields(rowData, client, true)

        columnKeys.forEach((key) => {
          if (
            key === 'clientName' ||
            key === 'name' ||
            key === 'clientEmail' ||
            key === 'email' ||
            key === 'entityType' ||
            key === 'entityName'
          ) {
            if (!rowData[key]) {
              rowData[key] = '-'
            }
            return
          }
          const value = client[key]
          rowData[key] =
            value !== null && value !== undefined && value !== '' ? value : '-'
        })

        allRows.push(
          <tr key={`client-${clientIndex}-empty`}>
            {columnKeys.map((columnKey) => (
              <td
                key={columnKey}
                style={{
                  ...(isSaffronColumn(columnKey) ? { color: '#bf9000' } : {}),
                  ...(isClientIdentityColumn(columnKey)
                    ? { cursor: 'pointer' }
                    : {}),
                  ...(columnKey === 'intentions'
                    ? { whiteSpace: 'pre-line', textAlign: 'left' }
                    : columnKey === 'insights'
                    ? { whiteSpace: 'pre-line' }
                    : {}),
                }}
                onClick={() => {
                  if (
                    isClientIdentityColumn(columnKey) &&
                    rowData[columnKey] &&
                    rowData[columnKey] !== ''
                  ) {
                    handleClientClick(clientId)
                  }
                }}>
                {rowData[columnKey] || '-'}
              </td>
            ))}
          </tr>,
        )
      }
    })

    return allRows
  }

  return (
    <Container
      className='account-report custom-offset-lg-1 custom-col-lg-12 px-0'
      fluid>
      <h2 className='text-center'>SESSION DETAILS REPORTS</h2>
      <Row className='m-0 d-lg-block'>
        <div className='col-xs-12'>
          <div className='table-outer-scroll'>
            <div className='table-inner-scroll'>
              <Table hover responsive className='custom-table'>
                <thead>
                  <tr>
                    {columnKeys.map((columnKey, index) => {
                      const columnTitle = columnMapping[columnKey]
                      return (
                        <th
                          key={`header-${index}`}
                          style={{ minWidth: '120px', fontSize: '12px' }}>
                          <ExpandableHeader
                            title={columnTitle}
                            maxLength={50}
                          />
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>{generateRows()}</tbody>
              </Table>
            </div>
          </div>
        </div>
      </Row>
    </Container>
  )
}

export default SessionDetailsTable
